import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import logo from "../assets/logo.png";

// Active link styling
const linkClass = ({ isActive }) =>
  `relative text-xs tracking-[0.1em] uppercase transition-all duration-200 ${
    isActive
      ? "text-white after:absolute after:left-0 after:-bottom-2 after:h-[2px] after:w-full after:bg-pink-500"
      : "text-white/60 hover:text-pink-300"
  }`;

const mobileLinkClass = ({ isActive }) =>
  `relative block py-3 text-sm tracking-[0.12em] uppercase transition-all duration-200 ${
    isActive
      ? "text-white after:absolute after:left-0 after:-bottom-[2px] after:h-[2px] after:w-full after:bg-pink-500"
      : "text-white/60 hover:text-pink-300"
  }`;

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <header
      className={[
        "fixed left-0 right-0 top-0 z-50",
        "transition-all duration-300 ease-out",
        scrolled
          ? "border-b border-pink-500/15 bg-black/95 backdrop-blur"
          : "border-b border-transparent bg-transparent",
      ].join(" ")}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img src={logo} alt="Kinews Studio" className="h-auto w-48 lg:w-60" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden items-center gap-8 md:flex">
          <NavLink to="/" className={linkClass} end>
            Home
          </NavLink>

          <NavLink to="/packages" className={linkClass}>
            Event Packages
          </NavLink>

          <NavLink to="/rentals" className={linkClass}>
            Studio Rentals
          </NavLink>

          <NavLink to="/gallery" className={linkClass}>
            Gallery
          </NavLink>

          {/* <NavLink to="/shop" className={linkClass}>
            Shop Frames
          </NavLink> */}

          <NavLink to="/contact" className={linkClass}>
            Contact
          </NavLink>
        </nav>

        {/* Desktop CTA */}
        <Link
          to="/book"
          className="hidden md:inline-flex rounded-full bg-pink-500 px-6 py-2.5 text-xs font-medium uppercase tracking-[0.08em] text-white shadow-[0_8px_30px_rgba(236,72,153,0.22)] transition-all duration-200 hover:-translate-y-[1px] hover:bg-pink-600 hover:shadow-[0_12px_40px_rgba(236,72,153,0.32)]"
        >
          Book Session
        </Link>

        {/* Mobile Hamburger */}
        <button
          type="button"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="relative flex h-10 w-10 items-center justify-center rounded-full border border-pink-500/25 bg-black/40 transition-colors hover:border-pink-500/50 md:hidden"
        >
          <span className="sr-only">{open ? "Close menu" : "Open menu"}</span>

          <span
            className={[
              "absolute h-[2px] w-5 bg-white transition-all duration-300 ease-out",
              open ? "translate-y-0 rotate-45" : "-translate-y-2",
            ].join(" ")}
          />
          <span
            className={[
              "absolute h-[2px] w-5 bg-white transition-all duration-300 ease-out",
              open ? "opacity-0" : "opacity-100",
            ].join(" ")}
          />
          <span
            className={[
              "absolute h-[2px] w-5 bg-white transition-all duration-300 ease-out",
              open ? "translate-y-0 -rotate-45" : "translate-y-2",
            ].join(" ")}
          />
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 z-40 bg-black/70"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />

            {/* Menu Panel */}
            <motion.div
              className="fixed left-0 right-0 top-[95px] z-50 border-t border-pink-500/15 bg-black md:hidden"
              initial={{ y: -12, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -12, opacity: 0 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <div className="mx-auto max-w-7xl px-6 py-6">
                <div className="space-y-1">
                  <NavLink to="/" className={mobileLinkClass} end>
                    Home
                  </NavLink>

                  <NavLink to="/packages" className={mobileLinkClass}>
                    Event Packages
                  </NavLink>

                  <NavLink to="/rentals" className={mobileLinkClass}>
                    Studio Rentals
                  </NavLink>

                  <NavLink to="/gallery" className={mobileLinkClass}>
                    Gallery
                  </NavLink>

                  {/* <NavLink to="/shop" className={mobileLinkClass}>
                    Shop Frames
                  </NavLink> */}

                  <NavLink to="/contact" className={mobileLinkClass}>
                    Contact
                  </NavLink>
                </div>

                {/* Mobile CTA */}
                <div className="mt-6">
                  <Link
                    to="/book"
                    className="inline-flex w-full items-center justify-center rounded-full bg-pink-500 px-6 py-3 text-xs font-medium uppercase tracking-[0.10em] text-white shadow-[0_8px_30px_rgba(236,72,153,0.22)] transition-all duration-200 hover:-translate-y-[1px] hover:bg-pink-600 hover:shadow-[0_12px_40px_rgba(236,72,153,0.32)]"
                  >
                    Book Session
                  </Link>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}
